import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQueryClient } from 'react-query';
import { CallIntelMocks } from '@frontend/api-call-intel';
import { MockPersons } from '@frontend/api-person';
import { DemoInterceptorUtils, FeatureMockRouter } from '@frontend/demo-interceptors';
import { MockInterceptorEntry, RequestInterceptorEntry, ResponseInterceptorEntry, useFetch } from '@frontend/fetch';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import { DEMO_PERSON } from './constant';

const INTERCEPTOR_ID_PREFIX = 'demo-contact-panel';
export type DemoContactPanelFeature = 'patient-insights' | 'call-intel-insights';

export interface UseDemoContactPanelInterceptorsProps {
  enabled: boolean;
  demoFeatures: DemoContactPanelFeature[];
  locationId?: string;
}

export const useDemoContactPanelInterceptors = ({
  enabled,
  demoFeatures,
  locationId: locationIdProp,
}: UseDemoContactPanelInterceptorsProps) => {
  const {
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    removeRequestInterceptor,
    removeResponseInterceptor,
    removeMockInterceptor,
  } = useFetch();

  const { lastUsedVertical: vertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { selectedLocationIds: scopedLocationIds, getScopeName } = useAppScopeStore();
  const [isDemoDataReady, setIsDemoDataReady] = useState(false);

  const queryClient = useQueryClient();
  const interceptorsInitializedRef = useRef(false);

  const locationId = locationIdProp ?? scopedLocationIds[0] ?? '';

  const requestInterceptors = useRef<RequestInterceptorEntry[]>([]);
  const responseInterceptors = useRef<ResponseInterceptorEntry[]>([]);
  const mockInterceptor = useRef<MockInterceptorEntry | null>(null);

  const interceptorRouters: FeatureMockRouter<CallIntelMocks.CallIntelDemoInterceptorsContext>[] = useMemo(() => {
    const routers: FeatureMockRouter<CallIntelMocks.CallIntelDemoInterceptorsContext>[] = [];

    if (demoFeatures.includes('patient-insights')) {
      routers.push(CallIntelMocks.ContactPanelMockRouter, CallIntelMocks.CallIntelRecordsMockRouter);
    }

    if (demoFeatures.includes('call-intel-insights')) {
      routers.push(CallIntelMocks.CallIntelInsightPanelMockRouter);
    }

    return routers;
  }, [demoFeatures]);

  const getMockDataContext = useCallback((): CallIntelMocks.CallIntelDemoInterceptorsContext => {
    const mockPersonsInstance = new MockPersons({ persons: [DEMO_PERSON] });
    return {
      vertical: vertical || Vertical.DENTAL,
      locationId,
      mockPersonsInstance,
      interceptorIdPrefix: INTERCEPTOR_ID_PREFIX,
      getScopeName,
      hasVoiceAiAccess: false,
    };
  }, [vertical, locationId, getScopeName]);

  const invalidateQueries = useCallback(() => {
    interceptorRouters.forEach((router) => router.invalidateQueries?.(queryClient));
  }, [interceptorRouters, queryClient]);

  const setupInterceptors = useCallback(() => {
    if (interceptorRouters.length === 0) return;

    try {
      const context = getMockDataContext();
      requestInterceptors.current = DemoInterceptorUtils.getRequestInterceptors(interceptorRouters, context);
      responseInterceptors.current = DemoInterceptorUtils.getResponseInterceptors(interceptorRouters, context);
      mockInterceptor.current = DemoInterceptorUtils.getMockRouter(interceptorRouters, context, INTERCEPTOR_ID_PREFIX);
      addMockInterceptor(mockInterceptor.current);
      requestInterceptors.current.forEach(addRequestInterceptor);
      responseInterceptors.current.forEach(addResponseInterceptor);
      invalidateQueries();
      interceptorsInitializedRef.current = true;
      setIsDemoDataReady(true);
    } catch (error) {
      sentry.warn({
        team: 'analytics',
        topic: 'analytics',
        error: 'Call Intel - Error setting up interceptors for demo contact panel',
        addContext: {
          name: 'DemoContactPanelInterceptorsSetup',
          context: {
            error,
          },
        },
      });
    }
  }, [
    getMockDataContext,
    interceptorRouters,
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    invalidateQueries,
  ]);

  const cleanup = useCallback(() => {
    if (interceptorsInitializedRef.current) {
      mockInterceptor.current?.id && removeMockInterceptor(mockInterceptor.current.id);
      requestInterceptors.current?.forEach(({ id }) => removeRequestInterceptor(id));
      responseInterceptors.current?.forEach(({ id }) => removeResponseInterceptor(id));
      invalidateQueries();
      interceptorsInitializedRef.current = false;
      setIsDemoDataReady(false);
    }
  }, [invalidateQueries, removeMockInterceptor, removeRequestInterceptor, removeResponseInterceptor]);

  useEffect(() => {
    cleanup();

    if (enabled) {
      setupInterceptors();
    }
    return () => {
      cleanup();
    };
  }, [cleanup, enabled, setupInterceptors]);

  return {
    isDemoDataReady: enabled && isDemoDataReady,
  };
};
